import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useQuery } from 'react-query';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { GetResourceGroupFn } from '../../data/apiCalls';
import {
	ACCOUNT_ACTIVATION_URL,
	LOGIN_URL,
} from '../../scripts/navigationItems';
import PageNotFound from '../Errors/PageNotFound';
import MarkdownLinkRenderer from '../shared/MarkdownLinkRenderer';
import MarkdownParagraphRenderer from '../shared/MarkdownParagraphRenderer';
import PageBanner from '../shared/Pages/PageBanner';
import PageBreadcrumbs from '../shared/Pages/PageBreadcrumbs';
import PageMobileTitle from '../shared/Pages/PageMobileTitle';
import {
	getResourceHeader,
	isMembers,
	isProvider,
} from '../../scripts/resourcesHelpers';
import useIsMobile from '../../hooks/useIsMobile';
import PageFailure from '../Errors/PageFailure';
import ResourceGroupPageSkeleton from './ResourceGroupPage.skeleton';
import ResourceGroupPageFooter from './ResourceGroupPageFooter';

const ResourceGroupPage = () => {
	const { palette } = useTheme();
	const { groupPageId } = useParams();
	const groupPageHeader = getResourceHeader(groupPageId);
	const isMobile = useIsMobile();

	const {
		data: {
			status,
			data: {
				resultCode,
				subResultCode,
				data: { result = {} } = {},
			} = {},
		} = {},
		isLoading,
		isError,
	} = useQuery(
		['ProviderResourceGroupType', groupPageId],
		() => GetResourceGroupFn(groupPageId),
		{
			staleTime: Infinity,
		}
	);

	const error =
		isError || (status >= 400 && status <= 505) || resultCode !== 0;
	const error404 = status === 404 || subResultCode === 9001;

	const [expanded, setExpanded] = useState(undefined);
	const handleChange = (isExpanded, panel) => {
		setExpanded(isExpanded ? panel : undefined);
	};

	const crumbs = [groupPageHeader, { link: '', text: result?.displayName }];

	const specialty = result?.displayName;

	const renderContent = () => {
		if (error404 || error) {
			return error404 ? <PageNotFound /> : <PageFailure />;
		}
		return (
			<>
				{isMobile ? (
					<PageMobileTitle title={groupPageHeader.text} />
				) : (
					isProvider(groupPageId) && (
						<PageBreadcrumbs crumbs={crumbs} />
					)
				)}
				<PageBanner
					error={error}
					imageUrl={result.bannerImageUrl}
					title={groupPageHeader.text}
					subtitle={result.displayName}
				/>
				<Typography variant='body1' sx={{ my: 3 }}>
					If you are a contracted provider,{' '}
					<Link href={LOGIN_URL} color='secondary'>
						log in
					</Link>{' '}
					or{' '}
					<Link href={ACCOUNT_ACTIVATION_URL} color='secondary'>
						activate your account
					</Link>{' '}
					to access additional resources.
				</Typography>
				<Box sx={{ mx: { xs: 1, md: 2 }, mb: { xs: 2, md: 3 } }}>
					<Typography
						component='h2'
						variant='h4'
						color='primary'
						data-testid='resource-group-page-title'
					>
						{result.displayName}
					</Typography>
					{result?.types?.map(type => {
						const id = type.displayName
							.replace(/ /g, '-')
							.toLowerCase();
						return (
							<Accordion
								key={id}
								expanded={expanded === id}
								onChange={(e, isExpanded) =>
									handleChange(isExpanded, id)
								}
								sx={{ mt: 2 }}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls={id}
								>
									<Typography component='h3' variant='h6'>
										{type.displayName}
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<ReactMarkdown
										components={{
											a: MarkdownLinkRenderer,
											p: MarkdownParagraphRenderer,
										}}
									>
										{type.overview}
									</ReactMarkdown>
								</AccordionDetails>
							</Accordion>
						);
					})}
				</Box>
				{/* Separate PBI - uncomment if wanted or destroy and create desired component(s) */}
				{isMembers(groupPageId) && (
					<ResourceGroupPageFooter groupPageId={groupPageId} />
				)}
				{specialty?.toLowerCase() !== 'members' && (
					<Box
						sx={{
							mb: { xs: 2, md: 3 },
							backgroundColor: palette.divider,
							height: '50px',
							display: 'flex',
							alignItems: 'center',
							paddingLeft: '16px', // Adjust the value as needed for indentation
						}}
					>
						<Typography variant='body1'>
							Have questions? Please visit our{' '}
							<Link
								component={RouterLink}
								to={{
									pathname: `/resources/${groupPageId}/faq`,
									state: { displayName: specialty },
								}}
								color='secondary'
							>
								FAQ
							</Link>
							.
						</Typography>
					</Box>
				)}
			</>
		);
	};

	return (
		<Container>
			{isLoading ? <ResourceGroupPageSkeleton /> : renderContent()}
		</Container>
	);
};

export default ResourceGroupPage;
